
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  NgZone,
  ViewChild,
  Inject,
  HostListener
} from "@angular/core";
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { UserProfile } from "../../models";
import { MailRootState } from "src/app/mail/store";
import { Store } from "@ngrx/store";
import { getUserProfile } from "../../../reducers";
import { filter, takeUntil, take } from "rxjs/operators";
import { Subject } from "rxjs/internal/Subject";
import { MailBroadcaster } from "../../../common/providers/mail-broadcaster.service";
import { MailService } from "../../../mail/shared/services/mail-service";
import { ToastService } from "../../../common/providers/toast.service";
import { MailConstants } from "../../../common/utils/mail-constants";
import { AppState } from "../../../reducers/app";
import { StartProcessing, StopProcessing, SetLastPhotoUpdate } from "../../../actions/app";
import { ElectronService } from "src/app/services/electron.service";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { ConfigService } from "src/app/config.service";

import { CommonRepository } from "src/app/mail/repositories/common-repository";
import { DefaultCover } from "src/app/contacts/models/default-cover.model";
import { CommonUtils } from "src/app/common/utils/common-util";
import { VNCImageCropperComponent } from "@uxf/vnc-library";
import { environment } from "src/environments/environment";
import { Utils } from "src/app/common";
import { DomSanitizer } from "@angular/platform-browser";

@Component({
  selector: "vp-avatar-upload-dialog",
  templateUrl: "./avatar-upload-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvatarUploadDialogComponent implements OnDestroy {
  currentUser: UserProfile;
  private isAlive$ = new Subject<boolean>();
  isMobileScreen: boolean = false;
  canUpdateAvatar: any;
  zimbraFeatureChangePasswordEnabled: boolean = true;
  scale = 0.9;
  min = 0.5;
  max = 1;
  indexItem = -1;
  @ViewChild(VNCImageCropperComponent) vncImageCropperComponent: VNCImageCropperComponent;
  defaultCovers: DefaultCover[] = [];
  hideCropper: boolean = false;
  defaultURL: string = "";
  showDefaultAvatar: boolean = false;
  oldAvatarUrl: string = "";
  visible: boolean = false;
  avatarSelected: boolean = false;
  cropperEvent: any;
  croppedImage: any = "";
  selectedFile: File | null = null;
  constructor(
    public dialogRef: MatDialogRef<AvatarUploadDialogComponent>,
    private store: Store<MailRootState>,
    private matDialog: MatDialog,
    private mailBroadcaster: MailBroadcaster,
    private mailService: MailService,
    private toastService: ToastService,
    private appStore: Store<AppState>,
    private ngZone: NgZone,
    private changeDetectionRef: ChangeDetectorRef,
    private electronService: ElectronService,
    private configService: ConfigService,
    private commonRepository: CommonRepository,
    private breakpointObserver: BreakpointObserver,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
      this.defaultURL = this.data.userProfile.avatar;
      if (!!this.defaultURL && this.defaultURL.startsWith(this.configService.get("avatarURL"))) {
          this.showDefaultAvatar = true;
          this.oldAvatarUrl = this.defaultURL;
          this.avatarSelected = false;
      }
    this.hideCropper = true;
    this.changeDetectionRef.markForCheck();
  }

    @HostListener("document:click", ["$event"])
    public onClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (target.classList.contains("LyImageCropper-defaultContent-f")) this.selectPhoto();

    }

    @HostListener("window:resize", ["$event"])
      onWindowResize() {
      if (window.innerWidth < 768) {
        this.dialogRef.close();
      }
    }

   ngOnInit() {
    for (let i = 1; i < 15; i++) {
        const text = i.toString().padStart(3, "0");
        this.defaultCovers.push({
            name: `default-pattern-sq-${text}.png`,
            url: this.sanitizer.bypassSecurityTrustResourceUrl(CommonUtils.getFullUrl(`/assets/avatar/default-pattern-sq-${text}.png`)),
        });
    }
    if (this.defaultCovers?.length) {
      this.getProfileAvatar();
    } else {
      setTimeout(() => {
          this.visible = true;
      }, 2000);
    }
    this.changeDetectionRef.markForCheck();

    this.isMobileScreen = this.breakpointObserver.isMatched("(max-width: 767px)");
    this.breakpointObserver
    .observe(["(max-width: 767px)"])
    .pipe(takeUntil(this.isAlive$))
    .subscribe((state: BreakpointState) => {
        if (state.matches) {
        this.isMobileScreen = true;
        } else {
            this.isMobileScreen = false;
        }

      setTimeout(() => {
          if (this.vncImageCropperComponent) {
            if (!this.defaultURL) {
              this.store.select(getUserProfile).pipe(filter(v => !!v), takeUntil(this.isAlive$)).subscribe((res: any) => {
                let avatarId = "";
                if (!!res) {
                  if (environment.isElectron) {
                    avatarId = this.electronService.md5(res.email);
                  } else {
                    avatarId = md5(res.email);
                  }
                  this.defaultURL = `${this.configService.get("avatarURL")}/${avatarId}.jpg`;
                  this.vncImageCropperComponent.cropper.loadImage({ originalDataURL: this.defaultURL });
                }
              });
            } else {
              this.vncImageCropperComponent.cropper.loadImage({ originalDataURL: this.defaultURL });
            }
          }
      }, 100);
    });
    if (environment.isCordova) {
        StatusBar.backgroundColorByHexString("#000000");
        StatusBar.styleBlackTranslucent();
    }
  }

  getProfileAvatar() {
      this.selectIndexItem(-1);
  }

  ngOnDestroy() {
    this.isAlive$.next(false);
    this.isAlive$.unsubscribe();
    if (environment.isCordova) {
      StatusBar.backgroundColorByHexString("#317bbc");
    }
  }

  close() {
    this.dialogRef.close();
  }

  selectPhoto() {
    if (!!document.getElementById("custom-input-select-avatar")) {
        (<HTMLElement>document.getElementById("custom-input-select-avatar")).click();

    }
  }

  loadedImageForCrop($event) {
    this.visible = true;
    this.avatarSelected = true;
    this.hideCropper = false;
    this.min = this.vncImageCropperComponent.cropper.scale;
    let center = 0;
    center = ((1 - this.vncImageCropperComponent.cropper.scale ) / 2 ) + this.vncImageCropperComponent.cropper.scale;
    this.scale = center;
    this.max = 1;
    this.changeDetectionRef.markForCheck();
  }

  imageCropError(event: any) {

  }

  imageCropped(event: any) {
    this.cropperEvent = event;
    this.croppedImage = event.dataURL;
  }

  removeSelectedImage() {
    this.hideCropper = true;
    this.vncImageCropperComponent.cropper.loadImage({ originalDataURL: "" });
  }

  cropImage() {
      if (this.defaultURL.startsWith("http")) {
          this.close();
      } else {
          this.vncImageCropperComponent.cropImage();
      }
  }

  selectIndexItem(index: number): void {
    if (index === -1) {
      this.visible = false;
      this.avatarSelected = true;
      this.hideCropper = false;
      setTimeout(() => {
        this.vncImageCropperComponent.cropper.loadImage({ originalDataURL: this.defaultURL });
      }, 1000);
      return;
    }
    if (index > -1) {
      this.visible = false;
      this.avatarSelected = true;
      this.indexItem = index;
      this.changeDetectionRef.markForCheck();
    }
    this.hideCropper = false;
    setTimeout(() => {
      if (index > -1) {
          this.defaultURL = this.defaultCovers[index].url?.changingThisBreaksApplicationSecurity;
          this.vncImageCropperComponent.cropper.loadImage({originalDataURL: this.defaultURL});
          const fileName = this.defaultCovers[index].name;
          Utils.dataUrlToFile(this.defaultURL, fileName).then((file: File) => {
            if (file) {
              this.selectedFile = file;
            }
          }).catch(err => {
            console.error("Error converting data URL to file:", err);
          });
        }
    }, 100);
    this.changeDetectionRef.markForCheck();
  }

  saveUpload() {
    if (this.selectedFile === null && this.croppedImage === "") {
      return;
    }
    const formData = new FormData();
    formData.append("file", this.selectedFile);
    console.log("avatarUpload", formData);
    this.appStore.dispatch(new StartProcessing());
    this.mailService
      .uploadAvatar(formData)
      .pipe(take(1))
      .subscribe(res => {
        this.appStore.dispatch(new StopProcessing());
        const avatarURL = this.configService.get("avatarURL");
        const currentTime = new Date().getTime();
        this.store.dispatch(new SetLastPhotoUpdate({email: this.data.userProfile.email, timestamp: currentTime}));
        const id = res.replace(/"/g, "");
        this.dialogRef.close(`${avatarURL}/${id}.jpg?v=${currentTime}`);
      }, err => {
        this.toastService.show(MailConstants.FAIL_AVTAR_UPLOADED_MSG);
        this.appStore.dispatch(new StopProcessing());
      });
  }

}
